import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { SpecialZones } from '../special-zones/utils';
import { ZoneCanvas } from '../special-zones/ZoneCanvas';
import {
  getMediaLayout,
  MediaLayout,
  OverlayMediaElement,
} from './special-zones-overlay-util';

const noop = () => undefined;

interface Props {
  mediaRef: RefObject<OverlayMediaElement | null>;
  specialZones?: SpecialZones;
}

export function SpecialZonesOverlay({ mediaRef, specialZones }: Props) {
  const { polygons = [], resolution } = specialZones ?? {};
  const overlayRootRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<MediaLayout | null>(null);

  // useLayoutEffect so the overlay is measured and committed before the
  // browser paints — avoids a visible "empty → laid-out" flash after the
  // user enables the zone overlay.
  useLayoutEffect(() => {
    if (!polygons.length) {
      return;
    }

    const mediaElement = mediaRef.current;
    const overlayRoot = overlayRootRef.current;
    const overlayContainer = overlayRoot?.parentElement;

    if (!mediaElement || !overlayRoot || !overlayContainer) {
      return;
    }

    const updateOverlay = () => {
      const rect = getMediaLayout(mediaElement, overlayRoot);
      if (rect) setLayout(rect);
    };

    const handleMediaLoad = () => updateOverlay();
    const resizeObserver = new ResizeObserver(updateOverlay);
    resizeObserver.observe(mediaElement);
    resizeObserver.observe(overlayContainer);

    if (mediaElement instanceof HTMLVideoElement) {
      mediaElement.addEventListener('loadedmetadata', handleMediaLoad);
    } else {
      // Images fire 'load' natively; the decoded-frame canvas (FramePlayer)
      // dispatches a synthetic 'load' after it paints a new resolution, so the
      // overlay re-measures without needing a remount.
      mediaElement.addEventListener('load', handleMediaLoad);
      if (mediaElement instanceof HTMLImageElement && mediaElement.complete) {
        handleMediaLoad();
      }
    }

    updateOverlay();

    return () => {
      resizeObserver.disconnect();

      if (mediaElement instanceof HTMLVideoElement) {
        mediaElement.removeEventListener('loadedmetadata', handleMediaLoad);
      } else {
        mediaElement.removeEventListener('load', handleMediaLoad);
      }
    };
  }, [mediaRef, polygons]);

  return (
    <div ref={overlayRootRef} className="pointer-events-none absolute inset-0">
      {layout && resolution && (
        <div
          className="absolute"
          style={{
            top: layout.top,
            left: layout.left,
            width: layout.width,
            height: layout.height,
          }}
        >
          <ZoneCanvas
            zones={polygons}
            externalDimensions={{ width: layout.width, height: layout.height }}
            externalImageResolution={resolution}
            readOnly
            selectedZoneId={null}
            onSelectZone={noop}
            onZoneChange={noop}
          />
        </div>
      )}
    </div>
  );
}

export default SpecialZonesOverlay;
